import { FC, useCallback, useState } from 'react'
import { MentionSentiment } from 'src/interfaces/topics.interface'
import { TopicsTableSection } from 'src/charts/TopicsSubtopics/TopicsTableSection'
import { REVIEW_TOPICS_COPY } from 'src/charts/TopicsSubtopics/copy'
import { useReviewAnalysisDateRange } from 'src/hooks/useReviewAnalysisDateRange'
import { TopicsOverviewSection } from 'src/charts/TopicsSubtopics/TopicsOverviewSection'
import {
  useGetReviewTopicAnalysisDetailQuery,
  useGetReviewTopicsAnalysisOverviewQuery,
  useGetReviewTopicsAnalysisQuery,
} from 'src/reactQuery/useGetReviewTopicsAnalysisQuery'

interface ReviewTopicsAnalysisProps {
  campaignId: string
  facilityId?: string
  platform?: string
  // Which widget to show. The brand page carries the overview card only (as
  // the Social Listening campaigns page does); a facility page carries the
  // table with its modal (as a Social Listening campaign page does).
  variant?: 'overview' | 'table'
  // One location of the facility (the facility page's Location filter).
  venueName?: string
}

/**
 * Topics & Subtopics for a review campaign: the same overview card, table,
 * modal and Topic › Subtopic drill-down Social Listening has, fed by the
 * review reads. A review may mention several topics, so shares are "of
 * reviews".
 */
export const ReviewTopicsAnalysis: FC<ReviewTopicsAnalysisProps> = ({
  campaignId,
  facilityId,
  platform,
  venueName,
  variant = 'table',
}) => {
  const range = useReviewAnalysisDateRange()
  const scope = { facilityId, platform, range, venueName }
  const isOverview = variant === 'overview'
  const [selection, setSelection] = useState<{
    sentiment: MentionSentiment | null
    subtopic: string | null
    topic: string | null
  }>({ sentiment: null, subtopic: null, topic: null })

  // Only the widget on show reads; the other query stays disabled.
  const overview = useGetReviewTopicsAnalysisOverviewQuery(
    isOverview ? campaignId : '',
    scope
  )
  const { data, isLoading } = useGetReviewTopicsAnalysisQuery(
    isOverview ? '' : campaignId,
    scope
  )
  const { data: detail, isLoading: detailLoading } =
    useGetReviewTopicAnalysisDetailQuery(
      isOverview ? '' : campaignId,
      selection.topic,
      selection.subtopic,
      scope,
      selection.sentiment
    )

  const onSelectionChange = useCallback(
    (
      topic: string | null,
      subtopic: string | null,
      sentiment: MentionSentiment | null
    ) =>
      setSelection((current) =>
        current.topic === topic &&
        current.subtopic === subtopic &&
        current.sentiment === sentiment
          ? current
          : { sentiment, subtopic, topic }
      ),
    []
  )

  if (isOverview) {
    return (
      <TopicsOverviewSection
        copy={REVIEW_TOPICS_COPY}
        data={overview.data}
        fluid
        isError={overview.isError}
        isLoading={overview.isLoading}
      />
    )
  }

  return (
    <TopicsTableSection
      copy={REVIEW_TOPICS_COPY}
      data={data}
      detailData={detail}
      detailLoading={detailLoading}
      isLoading={isLoading}
      onSelectionChange={onSelectionChange}
    />
  )
}
